import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../lib/axios';
import { Users, Baby, Plus, Calendar, Clock, Utensils, Moon, Droplet, Activity, ArrowLeft, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const ClassroomDetail = () => {
  const { classroomId } = useParams();
  const navigate = useNavigate();
  const [classroom, setClassroom] = useState(null);
  const [children, setChildren] = useState([]);
  const [dailyLogs, setDailyLogs] = useState({});
  const [loading, setLoading] = useState(true);
  const [presentStaff, setPresentStaff] = useState(1); // Mock data - needs real attendance
  const today = new Date().toISOString().split('T')[0];

  const getLogSummary = (childId) => {
    const log = dailyLogs[childId];
    if (!log) return { meals: 0, naps: 0, diapers: 0, bathroom: 0, activities: 0, hasArrival: false };
    
    return {
      meals: log.meals?.length || 0,
      naps: log.naps?.length || 0,
      diapers: log.diapers?.length || 0,
      bathroom: log.bathroom_logs?.length || 0,
      activities: log.activities?.length || 0,
      hasArrival: !!log.arrival_time,
    };
  };

  // Calculate Texas ratio compliance
  const getComplianceStatus = () => {
    const presentCount = children.filter(c => getLogSummary(c.id).hasArrival).length;
    
    // Texas ratios by age
    let requiredRatio = 1/4; // Default infant ratio 1:4
    if (classroom?.age_group?.includes('12-24')) {
      requiredRatio = 1/5; // Toddler 1:5
    } else if (classroom?.age_group?.includes('24-36')) {
      requiredRatio = 1/9; // 2-year-old 1:9
    } else if (classroom?.age_group?.includes('3-4') || classroom?.age_group?.includes('3-5')) {
      requiredRatio = 1/11; // Preschool 1:11
    }
    
    const requiredStaff = Math.ceil(presentCount * requiredRatio);
    const isCompliant = presentStaff >= requiredStaff;
    
    // Star rating calculation (simplified)
    let stars = 0;
    if (isCompliant) stars = 2; // Minimum 2 stars if compliant
    if (presentStaff > requiredStaff) stars = 3; // 3 stars if above minimum
    if (presentStaff >= requiredStaff + 1) stars = 4; // 4 stars if well-staffed
    
    return {
      isCompliant,
      presentCount,
      presentStaff,
      requiredStaff,
      stars,
      ratio: `${presentStaff}:${presentCount}`
    };
  };

  useEffect(() => {
    fetchClassroomData();
  }, [classroomId]);

  const fetchClassroomData = async () => {
    try {
      const [classroomRes, logsRes] = await Promise.all([
        axios.get(`/classrooms/${classroomId}`),
        axios.get(`/daily-logs-complete/classroom/${classroomId}/${today}`),
      ]);
      
      setClassroom(classroomRes.data);
      
      // Extract children and their logs
      const childrenData = logsRes.data.map(item => item.child);
      const logsMap = {};
      logsRes.data.forEach(item => {
        if (item.daily_log) {
          logsMap[item.child.id] = item.daily_log;
        }
      });
      
      setChildren(childrenData.sort((a, b) => a.name.localeCompare(b.name)));
      setDailyLogs(logsMap);
    } catch (error) {
      console.error('Error fetching classroom data:', error);
      toast.error('Failed to load classroom data');
    } finally {
      setLoading(false);
    }
  };

  const calculateAge = (dob) => {
    const birthDate = new Date(dob);
    const today = new Date();
    let months = (today.getFullYear() - birthDate.getFullYear()) * 12;
    months -= birthDate.getMonth();
    months += today.getMonth();
    
    if (months < 12) {
      return `${months}mo`;
    } else {
      const years = Math.floor(months / 12);
      const remainingMonths = months % 12;
      return remainingMonths > 0 ? `${years}y ${remainingMonths}mo` : `${years}y`;
    }
  };

  const compliance = classroom && children.length > 0 ? getComplianceStatus() : {
    isCompliant: true,
    presentCount: 0,
    presentStaff: 1,
    requiredStaff: 0,
    stars: 2,
    ratio: '1:0'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!classroom) {
    return (
      <div className="text-center py-12">
        <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-slate-900 mb-2">Classroom not found</h3>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="classroom-detail-page">
      {/* Back Button */}
      <Button 
        variant="ghost" 
        onClick={() => navigate(-1)}
        className="mb-2"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back
      </Button>

      {/* Header */}
      <div className="bg-white rounded-xl border border-slate-100 p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
              <Users className="w-8 h-8 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {classroom.name}
              </h1>
              <div className="flex items-center gap-4 mt-2">
                <span className="text-slate-600 flex items-center gap-1">
                  <Baby className="w-4 h-4" />
                  {children.length} children
                </span>
                <span className="text-slate-600">•</span>
                <span className="text-slate-600">{classroom.age_group}</span>
                {classroom.ratio_notes && (
                  <>
                    <span className="text-slate-600">•</span>
                    <span className="text-slate-600">{classroom.ratio_notes}</span>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2 px-4 py-2 bg-slate-50 rounded-lg">
              <Calendar className="w-4 h-4 text-slate-600" />
              <span className="text-sm font-medium text-slate-900">
                {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
              </span>
            </div>
          </div>
        </div>

        {/* Live Compliance Badge */}
        <div className="flex items-center gap-3 pt-4 border-t border-slate-100">
          {/* Attendance */}
          <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-lg">
            <Baby className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-semibold text-blue-900">
              Present: {compliance.presentCount} children
            </span>
          </div>
          
          <div className="flex items-center gap-2 px-3 py-2 bg-purple-50 rounded-lg">
            <Users className="w-4 h-4 text-purple-600" />
            <span className="text-sm font-semibold text-purple-900">
              Staff: {compliance.presentStaff} teacher{compliance.presentStaff !== 1 ? 's' : ''}
            </span>
          </div>

          {/* Ratio Compliance */}
          <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
            compliance.isCompliant ? 'bg-green-50' : 'bg-rose-50'
          }`}>
            <div className={`w-2 h-2 rounded-full ${compliance.isCompliant ? 'bg-green-600' : 'bg-rose-600'} animate-pulse`}></div>
            <span className={`text-sm font-semibold ${compliance.isCompliant ? 'text-green-900' : 'text-rose-900'}`}>
              Ratio: {compliance.ratio} {compliance.isCompliant ? '✓' : '⚠️'}
            </span>
          </div>

          {/* Texas Star Rating */}
          <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 rounded-lg">
            <span className="text-sm font-semibold text-amber-900">
              {'⭐'.repeat(compliance.stars)} {compliance.stars}-Star
            </span>
          </div>

          {/* Compliance Status */}
          {!compliance.isCompliant && (
            <div className="flex items-center gap-2 px-3 py-2 bg-rose-50 border border-rose-200 rounded-lg">
              <AlertTriangle className="w-4 h-4 text-rose-600" />
              <span className="text-sm font-semibold text-rose-900">
                Need {compliance.requiredStaff - compliance.presentStaff} more teacher{compliance.requiredStaff - compliance.presentStaff !== 1 ? 's' : ''}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* USDA Meal Tracking */}
      <div className="bg-white rounded-xl border border-slate-100 p-6">
        <h2 className="text-lg font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
          🍽️ USDA Meal Tracking
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
            <p className="text-3xl mb-1">☀️</p>
            <p className="text-2xl font-bold text-amber-900">
              {Object.values(dailyLogs).reduce((sum, log) => 
                sum + (log.meals?.filter(m => m.meal_type === 'breakfast').length || 0), 0)}
            </p>
            <p className="text-sm font-medium text-amber-700">Breakfast</p>
          </div>
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 text-center">
            <p className="text-3xl mb-1">🍪</p>
            <p className="text-2xl font-bold text-orange-900">
              {Object.values(dailyLogs).reduce((sum, log) => 
                sum + (log.meals?.filter(m => ['am_snack', 'snack_am', 'morning_snack'].includes(m.meal_type)).length || 0), 0)}
            </p>
            <p className="text-sm font-medium text-orange-700">AM Snack</p>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
            <p className="text-3xl mb-1">🍽️</p>
            <p className="text-2xl font-bold text-green-900">
              {Object.values(dailyLogs).reduce((sum, log) => 
                sum + (log.meals?.filter(m => m.meal_type === 'lunch').length || 0), 0)}
            </p>
            <p className="text-sm font-medium text-green-700">Lunch</p>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 text-center">
            <p className="text-3xl mb-1">🍎</p>
            <p className="text-2xl font-bold text-purple-900">
              {Object.values(dailyLogs).reduce((sum, log) => 
                sum + (log.meals?.filter(m => ['pm_snack', 'snack_pm', 'afternoon_snack', 'snack'].includes(m.meal_type)).length || 0), 0)}
            </p>
            <p className="text-sm font-medium text-purple-700">PM Snack</p>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl border border-slate-100 p-4">
          <div className="flex items-center gap-2 mb-1">
            <Clock className="w-5 h-5 text-blue-600" />
            <p className="text-sm font-medium text-slate-600">Present</p>
          </div>
          <p className="text-2xl font-bold text-slate-900">
            {children.filter(c => getLogSummary(c.id).hasArrival).length}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-slate-100 p-4">
          <div className="flex items-center gap-2 mb-1">
            <Utensils className="w-5 h-5 text-green-600" />
            <p className="text-sm font-medium text-slate-600">Total Meals</p>
          </div>
          <p className="text-2xl font-bold text-slate-900">
            {Object.values(dailyLogs).reduce((sum, log) => sum + (log.meals?.length || 0), 0)}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-slate-100 p-4">
          <div className="flex items-center gap-2 mb-1">
            <Moon className="w-5 h-5 text-purple-600" />
            <p className="text-sm font-medium text-slate-600">Naps</p>
          </div>
          <p className="text-2xl font-bold text-slate-900">
            {Object.values(dailyLogs).reduce((sum, log) => sum + (log.naps?.length || 0), 0)}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-slate-100 p-4">
          <div className="flex items-center gap-2 mb-1">
            <Droplet className="w-5 h-5 text-blue-600" />
            <p className="text-sm font-medium text-slate-600">Diapers</p>
          </div>
          <p className="text-2xl font-bold text-slate-900">
            {Object.values(dailyLogs).reduce((sum, log) => sum + (log.diapers?.length || 0), 0)}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-slate-100 p-4">
          <div className="flex items-center gap-2 mb-1">
            <Activity className="w-5 h-5 text-amber-600" />
            <p className="text-sm font-medium text-slate-600">Activities</p>
          </div>
          <p className="text-2xl font-bold text-slate-900">
            {Object.values(dailyLogs).reduce((sum, log) => sum + (log.activities?.length || 0), 0)}
          </p>
        </div>
      </div>

      {/* Children Roster */}
      <div className="bg-white rounded-xl border border-slate-100 p-6">
        <h2 className="text-xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Today&apos;s Roster
        </h2>
        
        <div className="space-y-3">
          {children.map((child) => {
            const summary = getLogSummary(child.id);
            return (
              <div
                key={child.id}
                className="p-4 bg-slate-50 rounded-lg border border-slate-100 hover:border-primary/20 transition-colors"
                data-testid={`roster-child-${child.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center">
                      <Baby className="w-5 h-5 text-primary" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Link
                          to={`/children/${child.id}`}
                          className="font-semibold text-slate-900 hover:text-primary"
                        >
                          {child.name}
                        </Link>
                        <span className="text-xs text-slate-500">{calculateAge(child.dob)}</span>
                        {summary.hasArrival && (
                          <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                            Present
                          </span>
                        )}
                        {child.allergies && (
                          <span className="px-2 py-0.5 bg-rose-100 text-rose-700 rounded-full text-xs font-medium">
                            ⚠️ {child.allergies}
                          </span>
                        )}
                      </div>
                      
                      {/* Activity Summary */}
                      <div className="flex items-center gap-4 mt-2 text-xs text-slate-600">
                        <span className="flex items-center gap-1">
                          <Utensils className="w-3 h-3" />
                          {summary.meals} meals
                        </span>
                        <span className="flex items-center gap-1">
                          <Moon className="w-3 h-3" />
                          {summary.naps} naps
                        </span>
                        {summary.diapers > 0 && (
                          <span className="flex items-center gap-1">
                            <Droplet className="w-3 h-3" />
                            {summary.diapers} diapers
                          </span>
                        )}
                        {summary.bathroom > 0 && (
                          <span className="flex items-center gap-1">
                            🚽 {summary.bathroom} bathroom
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Activity className="w-3 h-3" />
                          {summary.activities} activities
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="flex items-center gap-2">
                    <Link to={`/children/${child.id}`}>
                      <Button variant="outline" size="sm" className="rounded-full">
                        View Details
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {children.length === 0 && (
          <div className="text-center py-12">
            <Baby className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600">No children assigned to this classroom</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClassroomDetail;
